import { ApiConfigError, ApiRequestError, fetchSettings, listMilestones } from './api';
import { profile } from './config';
import { qs } from './dom';
import { mountRegressionLog } from './regression';
import { playSplash, SPLASH_DEFAULTS } from './splash';
import { createThemeToggle } from './theme';
import './regression.css';

const year = new Date().getFullYear();

function renderShell(settings: Record<string, string>): void {
  const app = qs<HTMLDivElement>('#app');
  const summary = settings['profile.summary'] ?? '';
  const tagline = settings['profile.tagline'] ?? profile.tagline;

  app.innerHTML = `
    <div class="rl-shell">
      <header class="rl-idcard">
        <div class="rl-idcard-top">
          <h1 class="rl-name-h1">${profile.name}</h1>
          <span id="theme-slot"></span>
        </div>
        <p class="rl-role">${profile.title}</p>
        <p class="rl-tagline">${tagline}</p>
        ${summary ? `<p class="rl-summary">${summary}</p>` : ''}
      </header>
      <main id="run"></main>
      <footer class="rl-contact">
        <div>Reach me:</div>
        <div class="rl-out">
          <a href="mailto:${profile.email}">${profile.email}</a>
          ${profile.links
            .map(
              (link) =>
                `<a href="${link.url}" target="_blank" rel="noopener noreferrer">${link.label}</a>`,
            )
            .join('')}
        </div>
        <div>$ <span class="rl-caret">▌</span></div>
        <div>© ${year} ${profile.name}</div>
      </footer>
    </div>
  `;

  qs<HTMLElement>('#theme-slot').replaceWith(createThemeToggle());
}

function renderLoading(container: HTMLElement): void {
  container.innerHTML = `<div class="rl-state" role="status">Collecting cases…</div>`;
}

function renderError(container: HTMLElement, error: unknown): void {
  const isConfig = error instanceof ApiConfigError;
  const message = isConfig
    ? error.message
    : error instanceof ApiRequestError
      ? `Couldn’t reach the CMS (${error.status}): ${error.message}`
      : 'Something went wrong collecting the run.';

  container.innerHTML = `
    <div class="rl-state rl-state-error">
      <div>✕ FAIL — ${message}</div>
      ${isConfig ? '' : '<button type="button" class="rl-flag retry-button">retry</button>'}
    </div>
  `;
  container.querySelector('.retry-button')?.addEventListener('click', () => {
    void init();
  });
}

async function init(): Promise<void> {
  // Settings first and on its own: the splash copy comes from it, so it has to
  // land before the animation starts.
  const settings = await fetchSettings();

  const splash = playSplash({
    command: settings['splash.command'] ?? SPLASH_DEFAULTS.command,
    response: settings['splash.response'] ?? SPLASH_DEFAULTS.response,
  });

  renderShell(settings);
  const container = qs<HTMLElement>('#run');
  renderLoading(container);

  // Fetch the run *behind* the splash so the animation is never dead time —
  // by the time it dissolves, the rows are usually already there.
  const milestones = listMilestones();

  try {
    const [, resolved] = await Promise.all([splash, milestones]);
    mountRegressionLog(container, resolved, settings);
  } catch (error) {
    await splash;
    renderError(container, error);
  }
}

void init();
